import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Action,
  Badge,
  Modal,
  RelativeDateTime,
  SimpleSelect,
  useToast,
} from 'corso-admin-ui';
import { commandStoreApi } from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCopyToClipboard } from 'usehooks-ts';
import { z } from 'zod';
import api from '~/api';
import { DataListCard, DataListEntry } from '~/components/DataList';
import env from '~/env';
import {
  useRefetchStore,
  useStoreContext,
  useStoreId,
} from '~/providers/StoreProvider';
import { enabledFeatureLabel } from '~/utils/enabledFeatureLabel';
import { StorePage } from '../StorePage';
import { ContactsCard } from './ContactsCard';
import { SlackCard } from './SlackCard';

const updateAgencySchema =
  commandStoreApi['/store/:storeId/agency'].put.request.body;

const NO_AGENCY = 'none';

function EditAgencyModal({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();
  const { agency } = useStoreContext();

  const agencies = useQuery({
    queryKey: ['agencies'],
    queryFn: () => api.agency.list(),
  });

  const { control, handleSubmit, reset } = useForm({
    resolver: zodResolver(updateAgencySchema),
    defaultValues: { agencyId: agency?.id ?? null },
  });

  const { mutate: updateAgency, isPending } = useMutation({
    mutationFn: (body: z.infer<typeof updateAgencySchema>) =>
      api.store(storeId).updateAgency(body),
    onSuccess: () => {
      reset();
      onClose();
      refetch();
      toast.show('Updated agency');
    },
    onError: () => toast.show('Failed to update agency'),
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((formValues) => updateAgency(formValues))(event).catch(
      console.error,
    );
  };

  const agencyOptions = [
    { label: 'No agency', value: NO_AGENCY },
    ...(agencies.data?.map((option) => ({
      label: option.name,
      value: `${option.id}`,
    })) ?? []),
  ];

  return (
    <Modal show={show} onClose={onClose} title="Edit agency">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Controller
          control={control}
          name="agencyId"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Agency"
              options={agencyOptions}
              value={field.value == null ? NO_AGENCY : `${field.value}`}
              onChange={(value) =>
                field.onChange(
                  value === NO_AGENCY ? null : Number.parseInt(value, 10),
                )
              }
              disabled={agencies.isPending}
              error={fieldState.error?.message}
            />
          )}
        />
        <Action variant="primary" type="submit" loading={isPending}>
          Save
        </Action>
      </form>
    </Modal>
  );
}

function StoreInfoCard() {
  const store = useStoreContext();

  const [showEditAgency, setShowEditAgency] = useState(false);

  const items = [
    { label: 'ID', value: `${store.id}`, copyEnabled: true },
    {
      label: 'Features',
      value: (
        <div className="flex flex-wrap gap-1">
          {store.enabledFeatures.map((feature) => (
            <Badge key={feature} variant="info">
              {enabledFeatureLabel[feature]}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      label: 'Storefront ID',
      value: store.externalStorefrontId ?? '—',
      copyEnabled: !!store.externalStorefrontId,
    },
    {
      label: 'Shopify Domain',
      value: store.myShopifyDomain,
      copyEnabled: true,
    },
    {
      label: 'App Installed',
      value: store.isAppInstalled ? (
        <Badge variant="success">True</Badge>
      ) : (
        <Badge variant="danger">False</Badge>
      ),
    },
    {
      label: 'Agency',
      value: (
        <div className="flex flex-row items-center gap-2">
          {store.agency?.name ? store.agency.name : 'No Agency'}

          <Action
            variant="ghost"
            icon="edit"
            accessibilityLabel="Edit Agency"
            onClick={() => setShowEditAgency(true)}
          />
        </div>
      ),
    },

    {
      label: 'Created',
      value: store.createdOn ? (
        <RelativeDateTime dateTime={store.createdOn} />
      ) : (
        '—'
      ),
    },
    {
      label: 'Updated',
      value: store.updatedOn ? (
        <RelativeDateTime dateTime={store.updatedOn} />
      ) : (
        '—'
      ),
    },
  ] satisfies DataListEntry[]; // for type inference
  return (
    <>
      <DataListCard items={items} />
      <EditAgencyModal
        show={showEditAgency}
        onClose={() => setShowEditAgency(false)}
      />
    </>
  );
}

export default function StoreInfoPage() {
  const store = useStoreContext();
  const toast = useToast();
  const [, copyToClipboard] = useCopyToClipboard();

  return (
    <StorePage
      title="Store info"
      primaryAction={{
        content: 'View In Merchant Admin',
        to: `${env.VITE_MERCHANT_APP_URL}/${store.id}`,
        target: '_blank',
      }}
      secondaryActions={[
        {
          id: 'copy-debug-session-url',
          content: 'Copy New Debug Session URL',
          onAction: () => {
            // last 5 base-32 chars change every ms, so each copy is a fresh, unused request
            const request = new Date().valueOf().toString(32).slice(-5);
            copyToClipboard(
              `${env.VITE_MERCHANT_APP_URL}/${store.id}?r=${request}`,
            )
              .then(() => toast.show('Copied new debug session URL'))
              .catch(console.error);
          },
        },
      ]}
    >
      <div className="flex flex-col gap-4">
        <StoreInfoCard />
        <SlackCard />
        <ContactsCard />
      </div>
    </StorePage>
  );
}
